import { Button, Icon, Text } from "@alpacahq/ui/old";

import { FAQ } from "../../../../components/common/FAQ";
import { Layout } from "../common/Layout";
import { TransferTypeButton } from "../common/TransferTypeButton";
import { GENERAL_FAQ } from "../faq";
import type { useWithdrawController } from "./Withdraw.controller";

export type WithdrawPresentationProps = Omit<
  ReturnType<typeof useWithdrawController>,
  "isLoading"
>;

export const WithdrawPresentation = ({
  onBackClicked,
  isACHOptionAllowed,
  achRelationship,
  lastTransferType,
  isUnlinkLoading,
  onACHClicked,
  wireRelationship,
  isWireRelationshipInternational,
  onWireClicked,
  isCurrencyCloudOptionAllowed,
  currencyCloudAccount,
  onCurrencyCloudClicked,
  unlinkModal,
}: WithdrawPresentationProps) => (
  <Layout
    title="Withdraw Funds"
    subtitle="Withdraw funds from your Alpaca account."
    className="p-2"
    faq={<FAQ faq={GENERAL_FAQ} />}
  >
    <Button variant="secondary" onClick={onBackClicked} className="mb-8">
      <Icon name="ArrowLeftCircle" className="mr-2" />
      Back
    </Button>

    <div className="pb-10">
      <Text className="pb-4 font-bold text-xl">
        Transfer to US bank accounts:
      </Text>
      <div className="flex flex-col gap-4">
        {isACHOptionAllowed && (
          <TransferTypeButton
            title="Direct Bank Transfer (ACH)"
            isRecommended={achRelationship?.status !== "REJECTED"}
            isHighlighted={lastTransferType === "ACH"}
            data={{
              estimated_arrival: "1 - 3 business days",
              fees: "Free",
              withdraw_limit: "$50,000",
              supported_banks: "US Domestic Banks",
            }}
            onClick={onACHClicked}
            linkedAccount={
              achRelationship
                ? {
                    account: achRelationship.account_name ?? "",
                    name: achRelationship.brand_name,
                    imgSrc: achRelationship.logo,
                    status:
                      achRelationship.status === "REJECTED"
                        ? { text: "Action Required", variant: "rose" }
                        : undefined,
                  }
                : undefined
            }
            isDisabled={isUnlinkLoading}
          />
        )}
        <TransferTypeButton
          title="Domestic Wire Transfer"
          isHighlighted={
            lastTransferType === "Wire" && !isWireRelationshipInternational
          }
          data={{
            estimated_arrival: "2 - 3 business days",
            fees: "$15",
            withdraw_limit: "None",
            supported_banks: "US Domestic Banks",
          }}
          onClick={onWireClicked()}
          linkedAccount={
            wireRelationship && !isWireRelationshipInternational
              ? {
                  account: wireRelationship.account_number,
                  name: wireRelationship.name,
                  status:
                    wireRelationship.status === "REJECTED"
                      ? { text: "Action Required", variant: "rose" }
                      : undefined,
                }
              : undefined
          }
          isDisabled={isUnlinkLoading}
        />
      </div>
      <Text className="pt-8 pb-4 font-bold text-xl">
        Transfer to international bank accounts:
      </Text>
      <div className="flex flex-col gap-4">
        {isCurrencyCloudOptionAllowed && (
          <TransferTypeButton
            title="Local Currency Transfer"
            isRecommended
            isHighlighted={lastTransferType === "CurrencyCloud"}
            data={{
              estimated_arrival: "1 - 3 business Days",
              fees: "Fees: 1.5% Fee (Max at $40)",
              withdraw_limit: "None",
              supported_banks: "Non-US Banks",
            }}
            onClick={onCurrencyCloudClicked}
            linkedAccount={
              currencyCloudAccount
                ? {
                    account: currencyCloudAccount.account_number,
                    name: currencyCloudAccount.bank_name,
                  }
                : undefined
            }
            isDisabled={isUnlinkLoading}
          />
        )}
        <TransferTypeButton
          title="International Wire Transfer"
          isHighlighted={
            lastTransferType === "Wire" && isWireRelationshipInternational
          }
          data={{
            estimated_arrival: "5 - 7 business days",
            fees: "$35",
            withdraw_limit: "None",
            supported_banks: "Non-US Banks",
          }}
          onClick={onWireClicked(true)}
          linkedAccount={
            wireRelationship && isWireRelationshipInternational
              ? {
                  account: wireRelationship.account_number,
                  name: wireRelationship.name,
                  status:
                    wireRelationship.status === "REJECTED"
                      ? { text: "Action Required", variant: "rose" }
                      : undefined,
                }
              : undefined
          }
          isDisabled={isUnlinkLoading}
        />
      </div>
    </div>
    {unlinkModal}
  </Layout>
);
